import { Component, ChangeDetectionStrategy, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { SubscriptionService } from '../../services/subscription.service';
import { FooterComponent } from '../footer/footer.component';
import { AuthHeaderComponent } from '../auth-header/auth-header.component';

@Component({
  selector: 'app-verify-email',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FooterComponent, AuthHeaderComponent],
  template: `
    <main>
      <app-auth-header></app-auth-header>

      <div class="verify-scroll">
      <div class="verify-container">
      <h1>E-Mail-Verifizierung</h1>
      
      <p class="info-text">
        Wir haben einen Verifizierungscode an <strong>{{ email }}</strong> gesendet.
        Bitte geben Sie den Code ein, um Ihre Registrierung abzuschließen.
      </p>

      <form [formGroup]="form" (ngSubmit)="onSubmit()">
        <div class="form-group">
          <label for="code">Verifizierungscode:</label>
          <input
            id="code"
            type="text"
            formControlName="code"
            placeholder="6-stelliger Code"
            maxlength="6"
            (input)="onCodeInput($event)"
          />
          @if (form.get('code')?.hasError('required') && form.get('code')?.touched) {
            <span class="error">Code ist erforderlich</span>
          }
          @if (form.get('code')?.hasError('minlength') && form.get('code')?.touched) {
            <span class="error">Code muss 6 Ziffern enthalten</span>
          }
        </div>

        @if (authService.errorMessage()) {
          <div class="error-message">
            {{ authService.errorMessage() }}
          </div>
        }

        @if (verificationSuccess()) {
          <div class="success-message">
            @if (hasSelectedPlan()) {
              E-Mail verifiziert! Sie werden zum Checkout weitergeleitet...
            } @else {
              E-Mail verifiziert! Sie werden zur Startseite weitergeleitet...
            }
          </div>
        }

        @if (!verificationSuccess()) {
          <button 
            type="button"
            (click)="onVerifyClick()"
            [disabled]="!form.valid || isLoading()"
          >
            @if (isLoading()) {
              Wird verifiziert...
            } @else {
              Verifizieren
            }
          </button>
        }
      </form>

      <div class="resend-section">
        <p>Code nicht erhalten?</p>
        <button 
          type="button" 
          (click)="onResendCode()"
          [disabled]="isResendLoading() || resendCooldown() > 0"
          class="resend-button"
        >
          @if (isResendLoading()) {
            Wird versendet...
          } @else if (resendCooldown() > 0) {
            Erneut versenden in {{ resendCooldown() }}s
          } @else {
            Code erneut versenden
          }
        </button>
      </div>

      <p class="login-link">
        <a href="/login">Zurück zum Login</a>
      </p>
    </div>
    </div>
    </main>

    <app-footer></app-footer>
  `,
  styles: [`
    :host {
      display: flex;
      flex-direction: column;
      height: 100vh;
      overflow: hidden;
    }

    main {
      flex: 1;
      width: 100%;
      display: flex;
      flex-direction: column;
      gap: 0;
      padding: 0;
      min-height: 0;
      box-sizing: border-box;
    }

    .verify-scroll {
      flex: 1;
      min-height: 0;
      overflow-y: auto;
      display: flex;
      flex-direction: column;
      padding: 22px 2rem 1.5rem;
      box-sizing: border-box;
    }

    main > div:not(.header-container) {
      padding: 0 2rem;
    }

    main > div:first-child {
      padding: 0;
    }

    .verify-container {
      max-width: 480px;
      margin: auto;
      padding: 40px;
      border: 1px solid #ccc;
      border-radius: 8px;
      background: #f9f9f9;
      width: 100%;
      box-sizing: border-box;
    }

    h1 {
      text-align: center;
      margin-bottom: 20px;
      color: #333;
    }

    .info-text {
      text-align: center;
      color: #666;
      margin-bottom: 30px;
      margin-top: -5px;
      font-size: 14px;
    }

    .info-text strong {
      color: #333;
    }

    .form-group {
      margin-bottom: 20px;
      margin-top: -5px;
      display: flex;
      flex-direction: column;
    }

    label {
      margin-bottom: 5px;
      color: #555;
      font-weight: 500;
    }

    input {
      padding: 10px;
      border: 1px solid #ddd;
      border-radius: 4px;
      font-size: 16px;
      font-family: monospace;
      text-align: center;
      letter-spacing: 4px;
    }

    input:focus {
      outline: none;
      border-color: #4CAF50;
      box-shadow: 0 0 5px rgba(76, 175, 80, 0.3);
    }

    .error {
      color: #d32f2f;
      font-size: 12px;
      margin-top: 5px;
    }

    .error-message {
      background-color: #ffebee;
      color: #d32f2f;
      padding: 10px;
      border-radius: 4px;
      margin-bottom: 15px;
      margin-top: -5px;
      border-left: 4px solid #d32f2f;
    }

    .success-message {
      background-color: #e8f5e9;
      color: #2e7d32;
      padding: 10px;
      border-radius: 4px;
      margin-bottom: 15px;
      border-left: 4px solid #2e7d32;
    }

    button {
      width: 100%;
      padding: 10px;
      background-color: #4CAF50;
      color: white;
      border: none;
      border-radius: 4px;
      font-size: 16px;
      font-weight: 500;
      cursor: pointer;
      transition: background-color 0.3s;
    }

    button:hover:not(:disabled) {
      background-color: #45a049;
    }

    button:disabled {
      background-color: #cccccc;
      cursor: not-allowed;
    }

    .resend-section {
      margin-top: 5px;
      padding-top: 5px;
      border-top: 1px solid #ddd;
      text-align: center;
    }

    .resend-section p {
      color: #666;
      margin-bottom: 10px;
      font-size: 14px;
    }

    .resend-button {
      background-color: #2196F3;
      font-size: 14px;
    }

    .resend-button:hover:not(:disabled) {
      background-color: #0b7dda;
    }

    .login-link {
      text-align: center;
      margin-top: 20px;
      font-size: 14px;
    }

    .login-link a {
      color: #4CAF50;
      text-decoration: none;
      font-weight: 500;
    }

    .login-link a:hover {
      text-decoration: underline;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VerifyEmailComponent {
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  protected authService = inject(AuthService);
  private subscriptionService = inject(SubscriptionService);

  email = '';
  verificationSuccess = signal(false);
  hasSelectedPlan = signal(false);
  isLoading = signal(false);
  isResendLoading = signal(false);
  resendCooldown = signal(0);
  storedPassword = '';

  form = this.fb.group({
    code: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(6)]]
  });

  constructor() {
    this.route.queryParams.subscribe(params => {
      this.email = params['email'] || '';
    });

    // Get password from session storage (gespeichert bei Registrierung)
    this.storedPassword = sessionStorage.getItem('registrationPassword') || '';
    console.log('Stored password found:', this.storedPassword ? 'YES' : 'NO');
    
    // Prüfe ob ein Plan ausgewählt wurde
    const selectedPlan = localStorage.getItem('selectedPlan');
    this.hasSelectedPlan.set(!!selectedPlan);
  }

  onSubmit() {
    console.log('=== onSubmit() aufgerufen ===');
    if (this.form.valid && this.email) {
      const { code } = this.form.value;
      
      // Debug-Ausgabe
      console.log('onSubmit called:', { email: this.email, code, hasPassword: !!this.storedPassword });
      
      if (code && this.storedPassword) {
        this.isLoading.set(true);
        this.authService.confirmRegistration(this.email, code, this.storedPassword).subscribe({
          next: (response) => {
            console.log('Verification erfolreich:', response);
            this.isLoading.set(false);
            this.verificationSuccess.set(true);
            sessionStorage.removeItem('registrationPassword');
            
            // Prüfe ob ein Plan ausgewählt wurde
            const selectedPlan = localStorage.getItem('selectedPlan');
            
            setTimeout(() => {
              if (selectedPlan === 'trial') {
                // Trial-Plan: Direkt aktivieren ohne Zahlung
                console.log('Trial-Plan erkannt - aktiviere 24h Testversion');
                this.subscriptionService.activateTrial().subscribe({
                  next: (response) => {
                    console.log('Trial aktiviert:', response);
                    // Lade User Features nach Trial-Aktivierung
                    this.subscriptionService.getUserFeatures().subscribe({
                      next: () => {
                        localStorage.removeItem('selectedPlan');
                        this.router.navigate(['/calculator']);
                      },
                      error: (err) => {
                        console.error('Features laden fehlgeschlagen:', err);
                        localStorage.removeItem('selectedPlan');
                        this.router.navigate(['/calculator']);
                      }
                    });
                  },
                  error: (err) => {
                    console.error('Trial-Aktivierung fehlgeschlagen:', err);
                    localStorage.removeItem('selectedPlan');
                    this.router.navigate(['/']);
                  }
                });
              } else if (selectedPlan) {
                // Bezahlter Plan: Zur Landing-Page mit Checkout
                localStorage.removeItem('selectedPlan');
                this.router.navigate(['/'], { 
                  queryParams: { plan: selectedPlan, openCheckout: 'true' },
                  fragment: 'pricing'
                });
              } else {
                // Kein Plan ausgewählt -> zu Dashboard/Home
                this.router.navigate(['/']);
              }
            }, 2000);
          },
          error: (err) => {
            console.error('Verification error:', err);
            this.isLoading.set(false);
          }
        });
      } else {
        console.error('Validierung fehlgeschlagen:', { code, storedPassword: this.storedPassword });
      }
    }
  }

  onVerifyClick() {
    console.log('=== onVerifyClick() aufgerufen ===');
    console.log('Form valid:', this.form.valid);
    console.log('Code value:', this.form.get('code')?.value);
    console.log('Email:', this.email);
    console.log('StoredPassword:', this.storedPassword ? 'vorhanden' : 'FEHLT!!!');
    this.onSubmit();
  }

  onCodeInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const cleanValue = input.value.replace(/\s/g, ''); // Leerzeichen entfernen
    if (cleanValue !== input.value) {
      input.value = cleanValue;
      this.form.get('code')?.setValue(cleanValue, { emitEvent: false });
    }
  }

  clearLocalStorage() {
    console.log('=== localStorage VOR dem Löschen ===');
    console.log(localStorage);
    localStorage.clear();
    sessionStorage.clear();
    console.log('=== localStorage NACH dem Löschen ===');
    console.log(localStorage);
    alert('localStorage und sessionStorage geleert!');
  }

  onResendCode() {
    if (this.email) {
      this.isResendLoading.set(true);
      this.authService.resendVerificationCode(this.email).subscribe({
        next: () => {
          this.isResendLoading.set(false);
          this.startResendCooldown();
        },
        error: (err) => {
          this.isResendLoading.set(false);
          console.error('Resend code error:', err);
        }
      });
    }
  }

  private startResendCooldown() {
    this.resendCooldown.set(60);
    const interval = setInterval(() => {
      const current = this.resendCooldown();
      if (current > 0) {
        this.resendCooldown.set(current - 1);
      } else {
        clearInterval(interval);
      }
    }, 1000);
  }
}
